/**
 * Kleurrekenwerk: lezen wat iemand intypt, en nagaan of het leesbaar blijft.
 *
 * Dit is de tegenhanger van `App\Support\Color\Hex` in de API. Beide kanten
 * moeten hetzelfde antwoord geven op "haalt deze combinatie 4,5:1?", want het
 * scherm waarschuwt terwijl je typt en de server corrigeert bij het renderen.
 * Lopen ze uiteen, dan ziet een eigenaar een groene vink en toch een andere
 * kleur op zijn rapport.
 *
 * Hier staan geen kleurwaarden: de kandidaten (wit, inkt) komen uit
 * `design-system/tokens.ts` en worden meegegeven. Wat hier binnenkomt is wat een
 * bedrijf zelf intypte — dat is de enige plek waar een kleurcode los van de
 * tokens bestaat.
 */

/** De ondergrens voor tekst uit principe 12. Niet onderhandelbaar. */
export const CONTRAST_FLOOR = 4.5;

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB = /^rgba?\(?\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i;

/**
 * Wat iemand intypte als `#RRGGBB`, of `null` als het geen kleur is.
 *
 * Neemt `#0e7c66`, `0E7C66`, `#abc` en `rgb(14, 124, 102)` aan — de vormen die
 * een ontwerper uit zijn eigen gereedschap plakt. Een alfawaarde in `rgba()`
 * gaat verloren: de API bewaart zes tekens, en een half doorzichtige huisstijl
 * bestaat niet.
 */
export function parseColor(value: string | null | undefined): string | null {
    const input = (value ?? '').trim();

    if (input === '') {
        return null;
    }

    const hex = HEX.exec(input)?.[1];

    if (hex !== undefined) {
        const full = hex.length === 3 ? hex.replace(/./g, (digit) => digit + digit) : hex;

        return `#${full.toUpperCase()}`;
    }

    const rgb = RGB.exec(input);

    if (rgb !== null) {
        return `#${[rgb[1], rgb[2], rgb[3]].map(toChannel).join('').toUpperCase()}`;
    }

    return null;
}

/** WCAG 2.x: 1 (gelijk) tot 21 (zwart op wit). */
export function contrastRatio(foreground: string, background: string): number {
    const first = relativeLuminance(foreground);
    const second = relativeLuminance(background);

    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/** Haalt deze tekst op deze ondergrond de ondergrens? */
export function isReadable(foreground: string, background: string): boolean {
    return contrastRatio(foreground, background) >= CONTRAST_FLOOR;
}

/** Welke van twee kandidaten het best leest op deze ondergrond. */
export function mostReadable(background: string, light: string, dark: string): string {
    return contrastRatio(light, background) >= contrastRatio(dark, background) ? light : dark;
}

/**
 * Dezelfde kleur, donker genoeg om op een lichte ondergrond gelezen te worden.
 *
 * Voor een kop of een link: de tint blijft herkenbaar, het woord wordt
 * leesbaar. `fallback` is wat er overblijft als zelfs bijna-zwart niet haalt.
 */
export function darkenUntilReadable(value: string, background: string, fallback: string): string {
    let colour = value;

    for (let step = 0; step < 20; step += 1) {
        if (isReadable(colour, background)) {
            return colour;
        }

        colour = darken(colour, 0.08);
    }

    return fallback;
}

function darken(value: string, amount: number): string {
    const factor = Math.max(0, 1 - amount);

    return `#${channels(value)
        .map((channel) => hex(Math.round(channel * factor)))
        .join('')
        .toUpperCase()}`;
}

function toChannel(value: string | undefined): string {
    return hex(Math.min(255, Math.max(0, Math.round(Number(value ?? 0)))));
}

function hex(channel: number): string {
    return channel.toString(16).padStart(2, '0');
}

function channels(value: string): [number, number, number] {
    const parsed = parseColor(value) ?? '#000000';

    return [
        Number.parseInt(parsed.slice(1, 3), 16),
        Number.parseInt(parsed.slice(3, 5), 16),
        Number.parseInt(parsed.slice(5, 7), 16),
    ];
}

function relativeLuminance(value: string): number {
    const linear = (channel: number): number => {
        const ratio = channel / 255;

        return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
    };

    const [red, green, blue] = channels(value);

    return 0.2126 * linear(red) + 0.7152 * linear(green) + 0.0722 * linear(blue);
}
